import { lazy, Suspense, useState } from "react";
import { ParentSize } from "@visx/responsive";
import type { StatsFilterParams } from "@/services/api/guardiansv2/types";
import { StatsCard } from "./StatsCard";
import { KpiCards } from "./KpiCards";
import { OutcomeSplitBars } from "./OutcomeSplitBars";
import { useGuardiansStats } from "./useGuardiansStats";

const FailureRateTrendChart = lazy(() =>
  import("./FailureRateTrendChart").then((m) => ({ default: m.FailureRateTrendChart }))
);
const CatalogueBreakdownChart = lazy(() =>
  import("./CatalogueBreakdownChart").then((m) => ({ default: m.CatalogueBreakdownChart }))
);
const TopIssuesChart = lazy(() =>
  import("./TopIssuesChart").then((m) => ({ default: m.TopIssuesChart }))
);
const AlertSeverityDonut = lazy(() =>
  import("./AlertSeverityDonut").then((m) => ({ default: m.AlertSeverityDonut }))
);

function ChartFallback() {
  return <div className="h-48 animate-pulse rounded-xl bg-white/5" />;
}

export interface StatsViewProps {
  filters: StatsFilterParams;
}

export function StatsView({ filters }: StatsViewProps) {
  const stats = useGuardiansStats(filters);
  const [catalogueExpanded, setCatalogueExpanded] = useState(false);

  if (stats.error) {
    return (
      <div className="p-6 text-sm text-red-300" data-testid="stats-error">
        {stats.error}
      </div>
    );
  }

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 pb-6"
      data-testid="guardians-stats-view"
    >
      <KpiCards overview={stats.overview} loading={stats.loading} />

      <StatsCard title="Screening outcomes over time" subtitle="Passed vs alerts raised">
        <Suspense fallback={<ChartFallback />}>
          <ParentSize debounceTime={50}>
            {({ width }) => (
              <FailureRateTrendChart
                width={width}
                height={280}
                points={stats.trend?.points ?? []}
                unit={stats.trend?.unit ?? "day"}
              />
            )}
          </ParentSize>
        </Suspense>
      </StatsCard>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <StatsCard
          title="Failures by rule catalogue"
          subtitle="Where rules apply — volume vs alert rate"
          action={
            stats.byCatalogue.length > 10 ? (
              <button
                type="button"
                className="text-xs text-indigo-300 transition-colors hover:text-indigo-200"
                onClick={() => setCatalogueExpanded((v) => !v)}
              >
                {catalogueExpanded ? "Show less" : "Show all"}
              </button>
            ) : null
          }
        >
          <Suspense fallback={<ChartFallback />}>
            <ParentSize debounceTime={50}>
              {({ width }) => (
                <CatalogueBreakdownChart
                  width={width}
                  height={catalogueExpanded ? 480 : 320}
                  data={stats.byCatalogue}
                  expanded={catalogueExpanded}
                />
              )}
            </ParentSize>
          </Suspense>
        </StatsCard>

        <StatsCard title="Top failing rules" subtitle="Most common rule and signal failures">
          <Suspense fallback={<ChartFallback />}>
            <ParentSize debounceTime={50}>
              {({ width }) => (
                <TopIssuesChart
                  width={width}
                  height={Math.max(320, Math.min(stats.issues.length, 12) * 36 + 56)}
                  data={stats.issues}
                />
              )}
            </ParentSize>
          </Suspense>
        </StatsCard>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <StatsCard title="Alert severity" subtitle="Red, amber, and unrated alerts in period">
          <Suspense fallback={<ChartFallback />}>
            <ParentSize debounceTime={50}>
              {({ width }) => (
                <AlertSeverityDonut width={width} height={240} data={stats.alerts} />
              )}
            </ParentSize>
          </Suspense>
        </StatsCard>

        <StatsCard title="Rule failures & funnel" subtitle="Origin breakdown and screening pipeline">
          <OutcomeSplitBars overview={stats.overview} />
        </StatsCard>
      </div>
    </div>
  );
}
